// Live reading — the judge's answer as it is written, not after it lands.
//
// The reading agent returns its whole verdict set through one forced
// `return_result` tool call, and pi-ai streams that call's arguments as raw
// text deltas (`toolcall_delta`). Those arguments are a JSON document whose
// `calls` array holds one complete, independently renderable entry per tool
// call the judge read, written in the order the chunk packed them — which is
// timeline order. So a reading can be WATCHED call by call instead of
// appearing all at once when the batch settles.
//
// Two pure jobs live here:
//
//   * createEntryScanner — an incremental brace-balance scanner that yields
//     complete `calls[]` entries out of a growing buffer. It handles the
//     DOUBLE-ENCODED form too (`result: "{\"calls\":[…]}"`), which is what
//     Claude-family models emit for a loosely-typed argument (see
//     coerceJsonArg). Without that branch the stream would silently never
//     fire on the provider most operators run.
//
//   * applyLiveEvents — folds a run's events into the render state the strip
//     and the live panel read.
//
// Why scan rather than read the partial the provider already parses: pi-ai
// keeps a `parseStreamingJson` view of the arguments so far on every delta's
// `partial`, but that view cannot tell a FINISHED entry from one still being
// written — a half-written verdict list reads as a short one. This surface may
// only show a call the judge is done with, so the boundary has to be the
// closing brace, which is what balancing finds and partial-parsing cannot.
//
// Streaming is an ACCELERATOR, never the source of truth. Every entry the
// scanner yields is re-derived from the settled response by runTraceReading,
// an entry it cannot parse is dropped rather than guessed at, and a batch that
// fails RETRACTS the calls it already streamed — a partial view must never
// leave a verdict on screen that the finished report does not contain.

// Tail of the model's own writing kept per batch, enough to show it working
// without turning the panel into a transcript.
const PREVIEW_TAIL = 140;

// ── the incremental entry scanner ────────────────────────────────────────────

// One token of the stream, and how many raw characters it spans. In the
// double-encoded form the inner JSON's own quotes and backslashes arrive as
// two-character escapes, so the structural characters the scanner balances
// are not at the offsets a naive per-character walk would find.
function nextToken(buf, i, escaped) {
  if (escaped && buf[i] === "\\") {
    const c = buf[i + 1];
    // An escape split across two deltas — wait for the rest rather than
    // mistaking a lone backslash for a structural character.
    if (c === undefined) return null;
    if (c === '"') return ['"', 2];
    if (c === "\\") return ["\\", 2];
    return [c, 2];
  }
  const c = buf[i];
  return c === undefined ? null : [c, 1];
}

// A complete entry's raw text back to a value. The double-encoded slice is a
// fragment of a JSON string body — only valid escapes, no literal control
// characters — so wrapping it in quotes and parsing twice is exact.
function parseEntry(raw, escaped) {
  try {
    return escaped ? JSON.parse(JSON.parse(`"${raw}"`)) : JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * A scanner over one `return_result` argument stream.
 *
 * `push(text)` appends a delta and returns the entries that COMPLETED on this
 * append — usually none, occasionally one. `reset()` starts over, which is
 * what a re-prompted child (the return-nudge path) needs: its second attempt
 * writes a fresh document, and the first attempt's fragment must not bleed in.
 */
export function createEntryScanner() {
  let buf = "";
  let armed = false;
  let escaped = false;
  let i = 0;
  let depth = 0;
  let start = -1;
  let inString = false;
  let pendingEsc = false;
  let closed = false;

  // Arm on the `calls` key in whichever spelling shows up first. The escaped
  // spelling cannot match the plain probe (its `s` is followed by a backslash),
  // so the two indices are unambiguous.
  const arm = () => {
    const plain = buf.indexOf('"calls"');
    const enc = buf.indexOf('\\"calls\\"');
    if (plain < 0 && enc < 0) return false;
    const useEnc = plain < 0 || (enc >= 0 && enc < plain);
    const open = buf.indexOf("[", useEnc ? enc : plain);
    if (open < 0) return false;
    escaped = useEnc;
    i = open + 1;
    armed = true;
    return true;
  };

  return {
    reset() {
      buf = "";
      armed = false;
      escaped = false;
      i = 0;
      depth = 0;
      start = -1;
      inString = false;
      pendingEsc = false;
      closed = false;
    },
    push(text) {
      if (closed || !text) return [];
      buf += String(text);
      if (!armed && !arm()) return [];
      const out = [];
      while (i < buf.length) {
        const tok = nextToken(buf, i, escaped);
        if (!tok) break;
        const [ch, w] = tok;
        const at = i;
        i += w;
        if (inString) {
          if (pendingEsc) pendingEsc = false;
          else if (ch === "\\") pendingEsc = true;
          else if (ch === '"') inString = false;
          continue;
        }
        if (ch === '"') { inString = true; continue; }
        if (ch === "{") {
          if (depth === 0) start = at;
          depth += 1;
          continue;
        }
        if (ch === "}") {
          depth -= 1;
          if (depth <= 0 && start >= 0) {
            const value = parseEntry(buf.slice(start, i), escaped);
            if (value && typeof value === "object") out.push(value);
            start = -1;
            depth = 0;
          }
          continue;
        }
        // The array closed: everything after it is the enclosing document.
        if (ch === "]" && depth === 0) { closed = true; break; }
      }
      return out;
    },
  };
}

// ── the live state ───────────────────────────────────────────────────────────

export function emptyLiveReading() {
  return {
    batches: [],
    // Judged calls in arrival order, in the exact shape finalizeReading takes
    // (plus the turn and tool name the dock lists them by). One list, uncapped:
    // the live marks come from the same code the finished report uses, and the
    // dock lists every call read rather than a window onto the last few.
    judged: [],
    // The turns whose calls were answered by the ledger. Painted at t=0 so
    // banked work never looks like work this run did.
    reusedTurns: [],
    totals: { reused: 0, calls: 0, accepted: 0, rejected: 0, additions: 0, discarded: 0 },
  };
}

// Every total except the reuse count is DERIVED from the judged set rather
// than accumulated, so a retraction — a failed batch, a re-prompted child —
// corrects the numbers by construction instead of needing its own bookkeeping.
function recountTotals(state) {
  let accepted = 0;
  let rejected = 0;
  let additions = 0;
  let discarded = 0;
  for (const j of state.judged) {
    for (const v of j.verdicts || []) {
      if (v.verdict === "accept") accepted += 1;
      else rejected += 1;
    }
    additions += (j.missing || []).length;
    discarded += j.discarded || 0;
  }
  return { ...state.totals, calls: state.judged.length, accepted, rejected, additions, discarded };
}

function withBatch(state, index, patch) {
  const batches = state.batches.map((b) => (b.index === index ? { ...b, ...patch } : b));
  return { ...state, batches };
}

/**
 * Fold one frame's worth of events into the live state. Coalescing is the
 * CALLER's job — activity events arrive per token, and a component that
 * re-rendered on each of them would spend the whole run reconciling.
 */
export function applyLiveEvents(state, events) {
  let next = state;
  let recount = false;
  for (const ev of events || []) {
    if (!ev) continue;
    switch (ev.t) {
      case "reused":
        next = {
          ...next,
          reusedTurns: ev.turns || [],
          totals: { ...next.totals, reused: ev.count || 0 },
        };
        break;
      case "batch": {
        const batch = {
          index: ev.index,
          from: ev.from,
          to: ev.to,
          size: ev.size,
          status: "reading",
          activity: "reading…",
          preview: "",
        };
        next = { ...next, batches: [...next.batches.filter((b) => b.index !== ev.index), batch] };
        break;
      }
      case "activity":
        next = withBatch(next, ev.index, {
          activity: ev.activity || "reading…",
          ...(ev.preview != null ? { preview: String(ev.preview).slice(-PREVIEW_TAIL) } : {}),
        });
        break;
      case "call": {
        // Last write wins on the id: a re-prompted child re-streams its whole
        // document, and the second attempt is the one that counts.
        const judged = next.judged.filter((j) => j.id !== ev.id);
        judged.push({
          id: ev.id,
          batch: ev.index,
          turn: ev.turn,
          name: ev.name,
          verdicts: ev.verdicts,
          missing: ev.missing,
          discarded: ev.discarded || 0,
        });
        next = { ...next, judged };
        recount = true;
        break;
      }
      case "batchreset": {
        // The child ended a turn without the forced call and was re-prompted:
        // its second attempt writes a whole new document, so the first
        // attempt's entries stop being this batch's answer.
        if (!next.judged.some((j) => j.batch === ev.index)) break;
        next = { ...next, judged: next.judged.filter((j) => j.batch !== ev.index) };
        recount = true;
        break;
      }
      case "batchdone": {
        // A failed batch takes its streamed calls back with it. Leaving them on
        // screen would show verdicts the settled report never contains.
        if (ev.status === "error") {
          next = { ...next, judged: next.judged.filter((j) => j.batch !== ev.index) };
          recount = true;
        }
        next = withBatch(next, ev.index, { status: ev.status, activity: ev.status === "error" ? "failed" : "done", preview: "" });
        break;
      }
      default:
        break;
    }
  }
  return recount ? { ...next, totals: recountTotals(next) } : next;
}
